import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  type ReactNode,
} from 'react';
import type { Customer, Vehicle } from '../types';
import { getTokens } from '../services/authApi';

// ---------- Session identity helpers ----------

/**
 * Decode the JWT access token to extract userId WITHOUT a library.
 * Returns empty string if token is missing or malformed.
 */
function getDecodedUserId(): string {
  try {
    const tokens = getTokens();
    if (!tokens?.accessToken) return '';
    const parts = tokens.accessToken.split('.');
    if (parts.length !== 3) return '';
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return String(payload.userId ?? payload.sub ?? payload.id ?? '');
  } catch {
    return '';
  }
}

/**
 * A random tag generated once per page load (module-level singleton).
 * Survives React StrictMode double-mount but NOT a full page refresh.
 * Used to scope sessionStorage so it won't restore after Ctrl+R.
 */
const SESSION_TAG: string = Math.random().toString(36).slice(2);

// ---------- AI Analysis Types ----------

export interface AISuggestion {
  value: unknown;
  confidence: number;
  band: 'blue' | 'yellow' | 'red' | 'required_missing';
  alternatives: Array<{ value: string; confidence: number; source: string }>;
  autoApplied: boolean;
}

type AIAnalysisPhase = 'idle' | 'analyzing' | 'review' | 'applied';

// ---------- State types ----------

type OrchestratorPhase =
  | 'idle'
  | 'customer_extraction'
  | 'customer_approval'
  | 'customer_save'
  | 'vehicle_approval'
  | 'vehicle_save'
  | 'complete';

export interface EstimateFormContextState {
  /** Whether the customer inline form has mounted and registered itself. */
  customerFormReady: boolean;
  /** Whether the vehicle inline form has mounted and registered itself. */
  vehicleFormReady: boolean;

  /** Persisted customer record after a successful save. */
  customerData: Customer | null;
  /** Persisted vehicle record after a successful save. */
  vehicleData: Vehicle | null;

  /** AI orchestration sub-state (legacy sequential orchestrator). */
  aiState: {
    customerFieldsApproved: Record<string, string> | null;
    vehicleFieldsApproved: Record<string, string> | null;
    orchestratorPhase: OrchestratorPhase;
    isProcessing: boolean;
  };

  /** New AI analysis panel state. */
  aiAnalysis: {
    phase: AIAnalysisPhase;
    /** Per-field suggestions keyed by field_path. */
    suggestions: Record<string, AISuggestion> | null;
    /** Field paths that are required but not found in notes. */
    requiredMissingPaths: string[];
    /** True when all required fields are blue or yellow. */
    allRequiredSatisfied: boolean;
    /** Which AI model provided the last result. */
    provider: 'groq' | 'openai' | null;
    /** Service items that have been approved and applied to the estimate. */
    serviceItems: any[];
    /** Service items awaiting user review/approval (confidence >= 0.60). */
    pendingServiceItems: any[];
    /** Whether user has reviewed the pending service items (approved or dismissed). */
    serviceItemsApproved: boolean;
    /** Total service items returned from API before confidence filtering. */
    rawServiceItemCount: number;
  };

  /** Field paths the user has manually edited (dirty = do not auto-overwrite). */
  dirtyFields: Set<string>;

  /**
   * Accumulated customer fields applied by AI — stored in context so they
   * survive DesktopEstimateForm remounts on resize.
   */
  pendingCustomerFields: Record<string, string>;

  /**
   * Accumulated vehicle fields applied by AI — stored in context so they
   * survive DesktopEstimateForm remounts on resize.
   */
  pendingVehicleFields: Record<string, string>;

  /**
   * Metadata fields set by AI that need to be applied to the outer form
   * (valid_until, customer_notes) — persisted here so they survive remounts.
   */
  pendingMetaFields: {
    customer_notes?: string;
    valid_until?: string;
  };

  /**
   * Per-field AI confidence info — used by CustomerInlineForm and VehicleInlineForm
   * to color-code their inputs (green/amber/red borders + confidence badges).
   * Keyed by full field path e.g. "customer.first_name", "vehicle.year".
   * Entry is removed when the user edits the field (MARK_FIELD_REVIEWED).
   */
  fieldConfidences: Record<string, { confidence: number; band: AISuggestion['band'] }>;

  /**
   * The field the user currently has focused, used by AICorrectionsChat to provide
   * context to the correction LLM so it knows which field the user is referring to.
   * null when no field is focused.
   */
  focusedField: { path: string; current_value: unknown } | null;
}

export interface EstimateFormContextActions {
  registerCustomerForm: () => void;
  registerVehicleForm: () => void;
  unregisterCustomerForm: () => void;
  unregisterVehicleForm: () => void;

  setCustomerData: (data: Customer) => void;
  setVehicleData: (data: Vehicle) => void;

  setAICustomerFields: (fields: Record<string, string>) => void;
  setAIVehicleFields: (fields: Record<string, string>) => void;
  transitionPhase: (phase: OrchestratorPhase) => void;

  onCustomerSaved: (customer: Customer) => void;
  onVehicleSaved: (vehicle: Vehicle) => void;
  onAIExtractionComplete: (data: unknown) => void;

  // New AI analysis actions
  startAIAnalysis: () => void;
  completeAIAnalysis: (payload: {
    suggestions: Record<string, AISuggestion>;
    requiredMissingPaths: string[];
    allRequiredSatisfied: boolean;
    provider: 'groq' | 'openai';
    serviceItems: any[];
  }) => void;
  errorAIAnalysis: () => void;
  markFieldDirty: (fieldPath: string) => void;
  /** Mark a suggestion as applied. Pass valueOverride to also update the displayed value (e.g. after inline edit). */
  applySuggestion: (fieldPath: string, valueOverride?: unknown) => void;
  dismissAIAnalysis: () => void;

  /** Accumulate a customer field that has been applied by the AI. */
  accumulateCustomerField: (key: string, value: string) => void;
  /** Accumulate a vehicle field that has been applied by the AI. */
  accumulateVehicleField: (key: string, value: string) => void;
  /** Accumulate a metadata field (customer_notes, valid_until). */
  accumulateMetaField: (key: keyof EstimateFormContextState['pendingMetaFields'], value: string) => void;
  /** Clear all pending accumulated fields (called after CustomerInlineForm/VehicleInlineForm apply them). */
  clearPendingFields: () => void;
  /**
   * Mark a field as "user-reviewed" — removes its confidence indicator so the
   * form input goes back to plain styling after the user edits it.
   */
  markFieldReviewed: (fieldPath: string) => void;
  /** Clear customerFieldsApproved so AI fill effect won't overwrite an accepted existing customer. */
  clearAICustomerFields: () => void;
  /** Clear vehicleFieldsApproved so AI fill effect won't overwrite an accepted existing vehicle. */
  clearAIVehicleFields: () => void;
  /** Approve pending service items — moves them from pendingServiceItems → serviceItems. */
  approveServiceItems: (approvedItems: any[]) => void;
  /** Dismiss all pending service items without adding them. */
  dismissServiceItems: () => void;
  /** Remove a single item from the pending list by index. */
  removePendingServiceItem: (index: number) => void;
  /** Report which field the user currently has focused (for AI correction context). */
  setFocusedField: (field: { path: string; current_value: unknown } | null) => void;
}

// ---------- Reducer ----------

type Action =
  | { type: 'REGISTER_CUSTOMER_FORM' }
  | { type: 'UNREGISTER_CUSTOMER_FORM' }
  | { type: 'REGISTER_VEHICLE_FORM' }
  | { type: 'UNREGISTER_VEHICLE_FORM' }
  | { type: 'SET_CUSTOMER_DATA'; payload: Customer }
  | { type: 'SET_VEHICLE_DATA'; payload: Vehicle }
  | { type: 'SET_AI_CUSTOMER_FIELDS'; payload: Record<string, string> }
  | { type: 'SET_AI_VEHICLE_FIELDS'; payload: Record<string, string> }
  | { type: 'TRANSITION_PHASE'; payload: OrchestratorPhase }
  | { type: 'CUSTOMER_SAVED'; payload: Customer }
  | { type: 'VEHICLE_SAVED'; payload: Vehicle }
  | { type: 'AI_EXTRACTION_COMPLETE'; payload: unknown }
  // New AI analysis actions
  | { type: 'AI_ANALYSIS_START' }
  | {
      type: 'AI_ANALYSIS_COMPLETE';
      payload: {
        suggestions: Record<string, AISuggestion>;
        requiredMissingPaths: string[];
        allRequiredSatisfied: boolean;
        provider: 'groq' | 'openai';
        serviceItems: any[];
      };
    }
  | { type: 'AI_ANALYSIS_ERROR' }
  | { type: 'MARK_FIELD_DIRTY'; payload: string }
  | { type: 'APPLY_SUGGESTION'; payload: string | { fieldPath: string; value?: unknown } }
  | { type: 'DISMISS_AI_ANALYSIS' }
  // Persistent accumulator actions
  | { type: 'ACCUMULATE_CUSTOMER_FIELD'; payload: { key: string; value: string } }
  | { type: 'ACCUMULATE_VEHICLE_FIELD'; payload: { key: string; value: string } }
  | { type: 'ACCUMULATE_META_FIELD'; payload: { key: string; value: string } }
  | { type: 'CLEAR_PENDING_FIELDS' }
  // Field confidence actions
  | { type: 'MARK_FIELD_REVIEWED'; payload: string }
  // Clear AI-approved fields (used when user picks an existing record from recommendation list)
  | { type: 'CLEAR_AI_CUSTOMER_FIELDS' }
  | { type: 'CLEAR_AI_VEHICLE_FIELDS' }
  // Service item approval actions
  | { type: 'APPROVE_SERVICE_ITEMS'; payload: any[] }
  | { type: 'DISMISS_SERVICE_ITEMS' }
  | { type: 'REMOVE_PENDING_SERVICE_ITEM'; payload: number }
  // Focused field for AI correction context
  | { type: 'SET_FOCUSED_FIELD'; payload: { path: string; current_value: unknown } | null };

const VALID_TRANSITIONS: Record<OrchestratorPhase, OrchestratorPhase[]> = {
  idle: ['customer_extraction', 'customer_approval'],
  customer_extraction: ['customer_approval', 'idle'],
  customer_approval: ['customer_save'],
  customer_save: ['vehicle_approval', 'complete'],
  vehicle_approval: ['vehicle_save'],
  vehicle_save: ['complete'],
  complete: ['idle'],
};

const isDev = process.env.NODE_ENV === 'development';

function reducer(
  state: EstimateFormContextState,
  action: Action,
): EstimateFormContextState {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.log('[EstimateFormContext]', action.type, 'payload' in action ? action.payload : '');
  }

  switch (action.type) {
    case 'REGISTER_CUSTOMER_FORM':
      return { ...state, customerFormReady: true };

    case 'UNREGISTER_CUSTOMER_FORM':
      return { ...state, customerFormReady: false };

    case 'REGISTER_VEHICLE_FORM':
      return { ...state, vehicleFormReady: true };

    case 'UNREGISTER_VEHICLE_FORM':
      return { ...state, vehicleFormReady: false };

    case 'SET_CUSTOMER_DATA':
      return { ...state, customerData: action.payload };

    case 'SET_VEHICLE_DATA':
      return { ...state, vehicleData: action.payload };

    case 'SET_AI_CUSTOMER_FIELDS':
      return {
        ...state,
        aiState: {
          ...state.aiState,
          customerFieldsApproved: action.payload,
        },
      };

    case 'SET_AI_VEHICLE_FIELDS':
      return {
        ...state,
        aiState: {
          ...state.aiState,
          vehicleFieldsApproved: action.payload,
        },
      };

    case 'TRANSITION_PHASE': {
      const currentPhase = state.aiState.orchestratorPhase;
      const nextPhase = action.payload;
      const allowed = VALID_TRANSITIONS[currentPhase];

      if (!allowed?.includes(nextPhase)) {
        if (isDev) {
          // eslint-disable-next-line no-console
          console.warn(
            `[EstimateFormContext] Invalid phase transition: ${currentPhase} -> ${nextPhase}`,
          );
        }
        return state;
      }

      return {
        ...state,
        aiState: {
          ...state.aiState,
          orchestratorPhase: nextPhase,
          isProcessing:
            nextPhase === 'customer_extraction' ||
            nextPhase === 'customer_save' ||
            nextPhase === 'vehicle_save',
        },
      };
    }

    case 'CUSTOMER_SAVED': {
      const hasVehicleFields = state.aiState.vehicleFieldsApproved !== null;
      const nextPhase: OrchestratorPhase = hasVehicleFields
        ? 'vehicle_approval'
        : 'complete';

      return {
        ...state,
        customerData: action.payload,
        aiState: {
          ...state.aiState,
          orchestratorPhase: nextPhase,
          isProcessing: false,
        },
      };
    }

    case 'VEHICLE_SAVED':
      return {
        ...state,
        vehicleData: action.payload,
        aiState: {
          ...state.aiState,
          orchestratorPhase: 'complete',
          isProcessing: false,
        },
      };

    case 'AI_EXTRACTION_COMPLETE':
      return {
        ...state,
        aiState: {
          ...state.aiState,
          isProcessing: false,
        },
      };

    // ── New AI analysis actions ──

    case 'AI_ANALYSIS_START':
      return {
        ...state,
        fieldConfidences: {},
        pendingCustomerFields: {},
        pendingVehicleFields: {},
        pendingMetaFields: {},
        aiState: {
          ...state.aiState,
          customerFieldsApproved: null,
          vehicleFieldsApproved: null,
        },
        aiAnalysis: {
          ...state.aiAnalysis,
          phase: 'analyzing',
          suggestions: null,
          requiredMissingPaths: [],
          allRequiredSatisfied: false,
          provider: null,
          serviceItems: [],
          pendingServiceItems: [],
          serviceItemsApproved: false,
          rawServiceItemCount: 0,
        },
      };

    case 'AI_ANALYSIS_COMPLETE': {
      const incomingSuggestions = action.payload.suggestions;

      // Build fieldConfidences, customerFieldsApproved, vehicleFieldsApproved, and pendingMetaFields
      // from ALL found fields (every band), so forms auto-fill directly without requiring
      // panel approval. autoApplied: true marks that the field has been pushed to the form.
      const newFieldConfidences: Record<string, { confidence: number; band: AISuggestion['band'] }> = {};
      const newCustomerFields: Record<string, string> = { ...(state.aiState.customerFieldsApproved ?? {}) };
      const newVehicleFields: Record<string, string> = { ...(state.aiState.vehicleFieldsApproved ?? {}) };
      const newMetaFields: EstimateFormContextState['pendingMetaFields'] = { ...state.pendingMetaFields };
      const markedSuggestions: Record<string, AISuggestion> = {};

      for (const [fp, s] of Object.entries(incomingSuggestions)) {
        const hasValue = s.value !== null && s.value !== undefined && String(s.value).trim() !== '';
        const isDirty = state.dirtyFields.has(fp);

        // Mark suggestion as autoApplied if not dirty and has a real value
        const shouldMark = hasValue && !isDirty && s.band !== 'required_missing';
        markedSuggestions[fp] = shouldMark ? { ...s, autoApplied: true } : s;

        // Record confidence for UI color-coding on the actual form inputs
        if (hasValue && s.band !== 'required_missing') {
          newFieldConfidences[fp] = { confidence: s.confidence, band: s.band };
        }

        // Populate ALL found customer fields into approved map (all bands)
        if (hasValue && !isDirty && fp.startsWith('customer.')) {
          const key = fp.replace('customer.', '');
          newCustomerFields[key] = String(s.value);
        }
        // Populate ALL found vehicle fields
        if (hasValue && !isDirty && fp.startsWith('vehicle.')) {
          const key = fp.replace('vehicle.', '');
          newVehicleFields[key] = String(s.value);
        }
        // Populate metadata fields directly into pendingMetaFields
        if (hasValue && !isDirty && fp === 'metadata.customer_notes') {
          newMetaFields.customer_notes = String(s.value);
        }
        if (hasValue && !isDirty && fp === 'metadata.valid_until') {
          newMetaFields.valid_until = String(s.value);
        }
      }

      const hasCustomerFields = Object.keys(newCustomerFields).length > 0;
      const hasVehicleFields = Object.keys(newVehicleFields).length > 0;

      return {
        ...state,
        fieldConfidences: newFieldConfidences,
        pendingCustomerFields: hasCustomerFields
          ? { ...state.pendingCustomerFields, ...newCustomerFields }
          : state.pendingCustomerFields,
        pendingVehicleFields: hasVehicleFields
          ? { ...state.pendingVehicleFields, ...newVehicleFields }
          : state.pendingVehicleFields,
        pendingMetaFields: newMetaFields,
        aiState: {
          ...state.aiState,
          customerFieldsApproved: hasCustomerFields ? newCustomerFields : state.aiState.customerFieldsApproved,
          vehicleFieldsApproved: hasVehicleFields ? newVehicleFields : state.aiState.vehicleFieldsApproved,
        },
        aiAnalysis: (() => {
          // Diagnostic logging — trace service items through the confidence filter
          const rawServiceItems = action.payload.serviceItems || [];
          const filteredServiceItems = rawServiceItems.filter(
            (item: any) => (item.confidence ?? 0) >= 0.60,
          );
          const droppedCount = rawServiceItems.length - filteredServiceItems.length;
          if (droppedCount > 0) {
            console.log(`[EstimateFormContext] Filtered out ${droppedCount} low-confidence service items:`,
              rawServiceItems.filter((item: any) => (item.confidence ?? 0) < 0.60)
                .map((item: any) => ({ desc: item.description, confidence: item.confidence }))
            );
          }
          console.log(`[EstimateFormContext] pendingServiceItems (${filteredServiceItems.length}):`,
            filteredServiceItems.map((item: any) => ({ desc: item.description, confidence: item.confidence }))
          );

          return {
            phase: 'review' as const,
            suggestions: markedSuggestions,
            requiredMissingPaths: action.payload.requiredMissingPaths,
            allRequiredSatisfied: action.payload.allRequiredSatisfied,
            provider: action.payload.provider,
            // Service items go to pendingServiceItems for user review, NOT serviceItems.
            // Filter out low-confidence items (< 0.60) — those are likely complaints, not tasks.
            serviceItems: [],
            pendingServiceItems: filteredServiceItems,
            serviceItemsApproved: false,
            rawServiceItemCount: rawServiceItems.length,
          };
        })(),
      };
    }

    case 'AI_ANALYSIS_ERROR':
      return {
        ...state,
        aiAnalysis: {
          ...state.aiAnalysis,
          phase: 'idle',
        },
      };

    case 'MARK_FIELD_DIRTY': {
      const newDirtyFields = new Set(state.dirtyFields);
      newDirtyFields.add(action.payload);
      return { ...state, dirtyFields: newDirtyFields };
    }

    case 'APPLY_SUGGESTION': {
      if (!state.aiAnalysis.suggestions) return state;
      // Payload is either a fieldPath string (legacy) or {fieldPath, value?} object
      const fieldPath = typeof action.payload === 'string'
        ? action.payload
        : action.payload.fieldPath;
      const valueOverride = typeof action.payload === 'string'
        ? undefined
        : action.payload.value;

      const existing = state.aiAnalysis.suggestions[fieldPath];
      if (!existing) return state;

      const updatedSuggestions = {
        ...state.aiAnalysis.suggestions,
        [fieldPath]: {
          ...existing,
          autoApplied: true,
          // If the user typed a custom value (inline edit), update .value so
          // the form field reflects the correct value after leaving edit mode.
          ...(valueOverride !== undefined ? { value: valueOverride } : {}),
        },
      };

      const allSatisfied = Object.entries(updatedSuggestions).every(
        ([, s]) => s.band !== 'required_missing' && s.band !== 'red',
      );

      return {
        ...state,
        aiAnalysis: {
          ...state.aiAnalysis,
          suggestions: updatedSuggestions,
          allRequiredSatisfied: allSatisfied,
        },
      };
    }

    case 'DISMISS_AI_ANALYSIS':
      return {
        ...state,
        fieldConfidences: {},
        aiAnalysis: {
          phase: 'idle',
          suggestions: null,
          requiredMissingPaths: [],
          allRequiredSatisfied: false,
          provider: null,
          serviceItems: [],
          pendingServiceItems: [],
          serviceItemsApproved: false,
          rawServiceItemCount: 0,
        },
        pendingCustomerFields: {},
        pendingVehicleFields: {},
        pendingMetaFields: {},
      };

    // ── Persistent accumulator actions ──

    case 'ACCUMULATE_CUSTOMER_FIELD':
      return {
        ...state,
        pendingCustomerFields: {
          ...state.pendingCustomerFields,
          [action.payload.key]: action.payload.value,
        },
        // Also update aiState.customerFieldsApproved so CustomerInlineForm sees it
        aiState: {
          ...state.aiState,
          customerFieldsApproved: {
            ...(state.aiState.customerFieldsApproved ?? {}),
            [action.payload.key]: action.payload.value,
          },
        },
      };

    case 'ACCUMULATE_VEHICLE_FIELD':
      return {
        ...state,
        pendingVehicleFields: {
          ...state.pendingVehicleFields,
          [action.payload.key]: action.payload.value,
        },
        // Also update aiState.vehicleFieldsApproved so VehicleInlineForm sees it
        aiState: {
          ...state.aiState,
          vehicleFieldsApproved: {
            ...(state.aiState.vehicleFieldsApproved ?? {}),
            [action.payload.key]: action.payload.value,
          },
        },
      };

    case 'ACCUMULATE_META_FIELD':
      return {
        ...state,
        pendingMetaFields: {
          ...state.pendingMetaFields,
          [action.payload.key]: action.payload.value,
        },
      };

    case 'CLEAR_PENDING_FIELDS':
      return {
        ...state,
        pendingCustomerFields: {},
        pendingVehicleFields: {},
        pendingMetaFields: {},
      };

    case 'CLEAR_AI_CUSTOMER_FIELDS':
      return {
        ...state,
        aiState: {
          ...state.aiState,
          customerFieldsApproved: null,
        },
      };

    case 'CLEAR_AI_VEHICLE_FIELDS':
      return {
        ...state,
        aiState: {
          ...state.aiState,
          vehicleFieldsApproved: null,
        },
      };

    // ── Service item approval actions ──

    case 'APPROVE_SERVICE_ITEMS':
      return {
        ...state,
        aiAnalysis: {
          ...state.aiAnalysis,
          serviceItems: action.payload,
          pendingServiceItems: [],
          serviceItemsApproved: true,
        },
      };

    case 'DISMISS_SERVICE_ITEMS':
      return {
        ...state,
        aiAnalysis: {
          ...state.aiAnalysis,
          pendingServiceItems: [],
          serviceItemsApproved: true,
        },
      };

    case 'REMOVE_PENDING_SERVICE_ITEM': {
      const idx = action.payload;
      const updated = state.aiAnalysis.pendingServiceItems.filter((_: any, i: number) => i !== idx);
      return {
        ...state,
        aiAnalysis: {
          ...state.aiAnalysis,
          pendingServiceItems: updated,
        },
      };
    }

    case 'MARK_FIELD_REVIEWED': {
      // User edited a field — remove its AI confidence indicator so the input
      // goes back to plain styling (no more green/amber/red border).
      const { [action.payload]: _removed, ...remainingConfidences } = state.fieldConfidences;
      return { ...state, fieldConfidences: remainingConfidences };
    }

    case 'SET_FOCUSED_FIELD':
      return { ...state, focusedField: action.payload };

    default:
      return state;
  }
}

// ---------- Session Storage persistence ----------

const SESSION_STORAGE_KEY = 'cnf_ai_analysis_state';

/** Serializes AI analysis state to sessionStorage (survives resize, NOT page refresh by design). */
function saveAIStateToSession(state: EstimateFormContextState): void {
  try {
    const toSave = {
      // Identity markers — verified on load to prevent cross-user / cross-page-refresh restore
      _userId: getDecodedUserId(),
      _sessionTag: SESSION_TAG,
      aiAnalysis: {
        ...state.aiAnalysis,
        // Keep in 'review' even after page interactions; 'analyzing' is transient
        phase: state.aiAnalysis.phase === 'analyzing' ? 'idle' : state.aiAnalysis.phase,
      },
      // dirtyFields: serialize Set as array
      dirtyFields: Array.from(state.dirtyFields),
      pendingCustomerFields: state.pendingCustomerFields,
      pendingVehicleFields: state.pendingVehicleFields,
      pendingMetaFields: state.pendingMetaFields,
      fieldConfidences: state.fieldConfidences,
      // aiState approved fields
      customerFieldsApproved: state.aiState.customerFieldsApproved,
      vehicleFieldsApproved: state.aiState.vehicleFieldsApproved,
    };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(toSave));
  } catch {
    // sessionStorage might be disabled; non-critical
  }
}

/**
 * Loads AI analysis state from sessionStorage.
 * ONLY restores if:
 *   1. The stored userId matches the currently-logged-in user's JWT userId.
 *   2. The stored sessionTag matches the current page-load SESSION_TAG.
 *      (SESSION_TAG is regenerated on every full page refresh, so Ctrl+R clears it.)
 * Returns null if either check fails — no auto-fill for wrong user or after page reload.
 */
function loadAIStateFromSession(): Partial<EstimateFormContextState> | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.aiAnalysis) return null;

    // Guard 1: session tag must match (prevents restore after page refresh)
    if (parsed._sessionTag !== SESSION_TAG) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }

    // Guard 2: userId must match current user (prevents cross-user restore)
    const currentUserId = getDecodedUserId();
    if (currentUserId && parsed._userId && parsed._userId !== currentUserId) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }

    return {
      aiAnalysis: parsed.aiAnalysis,
      dirtyFields: new Set<string>(parsed.dirtyFields ?? []),
      pendingCustomerFields: parsed.pendingCustomerFields ?? {},
      pendingVehicleFields: parsed.pendingVehicleFields ?? {},
      pendingMetaFields: parsed.pendingMetaFields ?? {},
      fieldConfidences: parsed.fieldConfidences ?? {},
      aiState: {
        customerFieldsApproved: parsed.customerFieldsApproved ?? null,
        vehicleFieldsApproved: parsed.vehicleFieldsApproved ?? null,
        orchestratorPhase: 'idle',
        isProcessing: false,
      },
    };
  } catch {
    return null;
  }
}

// ---------- Initial state ----------

const baseInitialState: EstimateFormContextState = {
  customerFormReady: false,
  vehicleFormReady: false,
  customerData: null,
  vehicleData: null,
  aiState: {
    customerFieldsApproved: null,
    vehicleFieldsApproved: null,
    orchestratorPhase: 'idle',
    isProcessing: false,
  },
  aiAnalysis: {
    phase: 'idle',
    suggestions: null,
    requiredMissingPaths: [],
    allRequiredSatisfied: false,
    provider: null,
    serviceItems: [],
    pendingServiceItems: [],
    serviceItemsApproved: false,
    rawServiceItemCount: 0,
  },
  dirtyFields: new Set<string>(),
  pendingCustomerFields: {},
  pendingVehicleFields: {},
  pendingMetaFields: {},
  fieldConfidences: {},
  focusedField: null,
};

function buildInitialState(): EstimateFormContextState {
  const persisted = loadAIStateFromSession();
  if (!persisted) return baseInitialState;

  return {
    ...baseInitialState,
    ...persisted,
  };
}

// ---------- Context ----------

const EstimateFormContext = createContext<
  (EstimateFormContextState & EstimateFormContextActions) | null
>(null);

// ---------- Provider ----------

interface EstimateFormProviderProps {
  children: ReactNode;
}

export function EstimateFormProvider({ children }: EstimateFormProviderProps) {
  const [state, dispatch] = useReducer(reducer, undefined, buildInitialState);

  // ── Persist AI state to sessionStorage only when relevant AI state changes ──
  // Scoped dep array prevents running on every render (was causing 653ms resize violations).
  useEffect(() => {
    const phase = state.aiAnalysis.phase;
    if (phase !== 'analyzing') {
      saveAIStateToSession(state);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.aiAnalysis.phase,
    state.aiAnalysis.suggestions,
    state.aiAnalysis.allRequiredSatisfied,
    state.dirtyFields,
    state.pendingCustomerFields,
    state.pendingVehicleFields,
    state.pendingMetaFields,
    state.fieldConfidences,
    state.aiState.customerFieldsApproved,
    state.aiState.vehicleFieldsApproved,
  ]);

  // ── Clear sessionStorage on page unload / navigation ──
  // The SESSION_TAG already handles page-refresh isolation, but this provides
  // belt-and-suspenders cleanup so stale data never accumulates.
  useEffect(() => {
    const handleUnload = () => {
      try { sessionStorage.removeItem(SESSION_STORAGE_KEY); } catch { /* ignore */ }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  const registerCustomerForm = useCallback(
    () => dispatch({ type: 'REGISTER_CUSTOMER_FORM' }),
    [],
  );
  const unregisterCustomerForm = useCallback(
    () => dispatch({ type: 'UNREGISTER_CUSTOMER_FORM' }),
    [],
  );
  const registerVehicleForm = useCallback(
    () => dispatch({ type: 'REGISTER_VEHICLE_FORM' }),
    [],
  );
  const unregisterVehicleForm = useCallback(
    () => dispatch({ type: 'UNREGISTER_VEHICLE_FORM' }),
    [],
  );

  const setCustomerData = useCallback(
    (data: Customer) => dispatch({ type: 'SET_CUSTOMER_DATA', payload: data }),
    [],
  );
  const setVehicleData = useCallback(
    (data: Vehicle) => dispatch({ type: 'SET_VEHICLE_DATA', payload: data }),
    [],
  );

  const setAICustomerFields = useCallback(
    (fields: Record<string, string>) =>
      dispatch({ type: 'SET_AI_CUSTOMER_FIELDS', payload: fields }),
    [],
  );
  const setAIVehicleFields = useCallback(
    (fields: Record<string, string>) =>
      dispatch({ type: 'SET_AI_VEHICLE_FIELDS', payload: fields }),
    [],
  );
  const transitionPhase = useCallback(
    (phase: OrchestratorPhase) =>
      dispatch({ type: 'TRANSITION_PHASE', payload: phase }),
    [],
  );

  const onCustomerSaved = useCallback(
    (customer: Customer) =>
      dispatch({ type: 'CUSTOMER_SAVED', payload: customer }),
    [],
  );
  const onVehicleSaved = useCallback(
    (vehicle: Vehicle) =>
      dispatch({ type: 'VEHICLE_SAVED', payload: vehicle }),
    [],
  );
  const onAIExtractionComplete = useCallback(
    (data: unknown) =>
      dispatch({ type: 'AI_EXTRACTION_COMPLETE', payload: data }),
    [],
  );

  const startAIAnalysis = useCallback(
    () => dispatch({ type: 'AI_ANALYSIS_START' }),
    [],
  );
  const completeAIAnalysis = useCallback(
    (payload: {
      suggestions: Record<string, AISuggestion>;
      requiredMissingPaths: string[];
      allRequiredSatisfied: boolean;
      provider: 'groq' | 'openai';
      serviceItems: any[];
    }) => dispatch({ type: 'AI_ANALYSIS_COMPLETE', payload }),
    [],
  );
  const errorAIAnalysis = useCallback(
    () => dispatch({ type: 'AI_ANALYSIS_ERROR' }),
    [],
  );
  const markFieldDirty = useCallback(
    (fieldPath: string) => dispatch({ type: 'MARK_FIELD_DIRTY', payload: fieldPath }),
    [],
  );
  const applySuggestion = useCallback(
    (fieldPath: string, valueOverride?: unknown) =>
      dispatch({
        type: 'APPLY_SUGGESTION',
        payload: valueOverride !== undefined ? { fieldPath, value: valueOverride } : fieldPath,
      }),
    [],
  );
  const dismissAIAnalysis = useCallback(
    () => {
      dispatch({ type: 'DISMISS_AI_ANALYSIS' });
      // Clear sessionStorage when user explicitly dismisses
      try { sessionStorage.removeItem(SESSION_STORAGE_KEY); } catch { /* ignore */ }
    },
    [],
  );

  const accumulateCustomerField = useCallback(
    (key: string, value: string) =>
      dispatch({ type: 'ACCUMULATE_CUSTOMER_FIELD', payload: { key, value } }),
    [],
  );
  const accumulateVehicleField = useCallback(
    (key: string, value: string) =>
      dispatch({ type: 'ACCUMULATE_VEHICLE_FIELD', payload: { key, value } }),
    [],
  );
  const accumulateMetaField = useCallback(
    (key: keyof EstimateFormContextState['pendingMetaFields'], value: string) =>
      dispatch({ type: 'ACCUMULATE_META_FIELD', payload: { key, value } }),
    [],
  );
  const clearPendingFields = useCallback(
    () => dispatch({ type: 'CLEAR_PENDING_FIELDS' }),
    [],
  );
  const markFieldReviewed = useCallback(
    (fieldPath: string) => dispatch({ type: 'MARK_FIELD_REVIEWED', payload: fieldPath }),
    [],
  );
  const clearAICustomerFields = useCallback(
    () => dispatch({ type: 'CLEAR_AI_CUSTOMER_FIELDS' }),
    [],
  );
  const clearAIVehicleFields = useCallback(
    () => dispatch({ type: 'CLEAR_AI_VEHICLE_FIELDS' }),
    [],
  );
  const approveServiceItems = useCallback(
    (approvedItems: any[]) => dispatch({ type: 'APPROVE_SERVICE_ITEMS', payload: approvedItems }),
    [],
  );
  const dismissServiceItems = useCallback(
    () => dispatch({ type: 'DISMISS_SERVICE_ITEMS' }),
    [],
  );
  const removePendingServiceItem = useCallback(
    (index: number) => dispatch({ type: 'REMOVE_PENDING_SERVICE_ITEM', payload: index }),
    [],
  );
  const setFocusedField = useCallback(
    (field: { path: string; current_value: unknown } | null) =>
      dispatch({ type: 'SET_FOCUSED_FIELD', payload: field }),
    [],
  );

  const value = useMemo(
    () => ({
      ...state,
      registerCustomerForm,
      unregisterCustomerForm,
      registerVehicleForm,
      unregisterVehicleForm,
      setCustomerData,
      setVehicleData,
      setAICustomerFields,
      setAIVehicleFields,
      transitionPhase,
      onCustomerSaved,
      onVehicleSaved,
      onAIExtractionComplete,
      startAIAnalysis,
      completeAIAnalysis,
      errorAIAnalysis,
      markFieldDirty,
      applySuggestion,
      dismissAIAnalysis,
      accumulateCustomerField,
      accumulateVehicleField,
      accumulateMetaField,
      clearPendingFields,
      markFieldReviewed,
      clearAICustomerFields,
      clearAIVehicleFields,
      approveServiceItems,
      dismissServiceItems,
      removePendingServiceItem,
      setFocusedField,
    }),
    [
      state,
      registerCustomerForm,
      unregisterCustomerForm,
      registerVehicleForm,
      unregisterVehicleForm,
      setCustomerData,
      setVehicleData,
      setAICustomerFields,
      setAIVehicleFields,
      transitionPhase,
      onCustomerSaved,
      onVehicleSaved,
      onAIExtractionComplete,
      startAIAnalysis,
      completeAIAnalysis,
      errorAIAnalysis,
      markFieldDirty,
      applySuggestion,
      dismissAIAnalysis,
      accumulateCustomerField,
      accumulateVehicleField,
      accumulateMetaField,
      clearPendingFields,
      markFieldReviewed,
      clearAICustomerFields,
      clearAIVehicleFields,
      approveServiceItems,
      dismissServiceItems,
      removePendingServiceItem,
      setFocusedField,
    ],
  );

  return (
    <EstimateFormContext.Provider value={value}>
      {children}
    </EstimateFormContext.Provider>
  );
}

// ---------- Hook ----------

/**
 * Access the unified estimate form context.
 * Must be called within an <EstimateFormProvider>.
 */
export function useEstimateFormContext(): EstimateFormContextState &
  EstimateFormContextActions {
  const ctx = useContext(EstimateFormContext);
  if (!ctx) {
    throw new Error(
      'useEstimateFormContext must be used within an EstimateFormProvider',
    );
  }
  return ctx;
}
